// TabBar — Home Tab + 5-slot IA. Single job: render the bottom 5-slot bar
// (HOME · BETS · IMPORT-raised · RESEARCH · HISTORY) and route taps to the
// caller. Purely presentational — no state, no hooks, no haptics, no API. The
// caller owns haptics: onNavigate/onImport are pre-wrapped in App.js. Nav slots
// map 1:1 to App.js page indices (HOME→0, BETS→1, RESEARCH→2, HISTORY→3); IMPORT
// is a modal trigger with no page. Glyph SVGs are lifted verbatim from the
// Jumbotron/Home mockup (spec §Design) — do not redraw. Reads: activePage.
// Writes: nothing.
import { JT } from "./jumbotronTokens";

// Verbatim glyphs (spec §Design). currentColor inherits the button's text color
// so the active/inactive palette drives the glyph. The BETS front card hard-codes
// fill="#050d1a" to occlude the back card — keep in sync with JT.bg.root.
const GLYPHS = {
  HOME: (
    <svg width="24" height="24" viewBox="0 0 22 22" fill="none">
      <path d="M3.5 10.5 L11 4 L18.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 10 V16.5 C6 17.5 6.5 18 7.5 18 H14.5 C15.5 18 16 17.5 16 16.5 V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M9.5 18 V14 H12.5 V18" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  ),
  BETS: (
    <svg width="24" height="24" viewBox="0 0 22 22" fill="none">
      <rect x="4" y="4" width="11" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <rect x="7" y="2" width="11" height="14" rx="1.5" fill="#050d1a" stroke="currentColor" strokeWidth="1.5" />
      <path d="M9.5 6 H16 M9.5 9 H16 M9.5 12 H13" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  IMPORT: (
    <svg width="24" height="24" viewBox="0 0 22 22" fill="none">
      <rect x="5" y="2.5" width="12" height="17" rx="2.2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M11 6 V13 M8 10 L11 13 L14 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="9.5" y="16.5" width="3" height="1.2" rx=".6" fill="currentColor" />
    </svg>
  ),
  RESEARCH: (
    <svg width="24" height="24" viewBox="0 0 22 22" fill="none">
      <rect x="3" y="13" width="3.4" height="6" rx=".7" fill="currentColor" />
      <rect x="9.3" y="9" width="3.4" height="10" rx=".7" fill="currentColor" />
      <rect x="15.6" y="5" width="3.4" height="14" rx=".7" fill="currentColor" />
      <path d="M4 3 L11 8 L18 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  HISTORY: (
    <svg width="24" height="24" viewBox="0 0 22 22" fill="none">
      <circle cx="11" cy="11" r="7.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M11 7 V11 L14 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

// DESIGN.md Label role (11px floor) -- UI shell + token floor, 2026-09-20.
const LABEL_STYLE = JT.type.label;

export default function TabBar({ activePage, onNavigate, onImport }) {
  // Explicit active mapping (spec §6). Page 4 (Bookies, entered from the header) lights
  // NO slot -- the bar must not claim BETS is active while the user is on Bookies (five-tab
  // critique 2026-09-20, interim until #1071 gives Bookies a real home). Any other unknown
  // page still falls back to BETS so the bar is never rendered orphaned.
  const activeSlot =
    activePage === 0 ? "HOME" :
    activePage === 1 ? "BETS" :
    activePage === 2 ? "RESEARCH" :
    activePage === 3 ? "HISTORY" :
    activePage === 4 ? null : "BETS";

  // Visual order left→right. IMPORT is the raised centre modal trigger (no page).
  const navSlots = [
    { label: "HOME", page: 0 },
    { label: "BETS", page: 1 },
    { label: "RESEARCH", page: 2 },
    { label: "HISTORY", page: 3 },
  ];
  const navBtn = ({ label, page }) => {
    const isActive = activeSlot === label;
    return (
      // aria-current="page" is the active state for a navigation landmark (aria-selected on a
      // <button> is invalid ARIA; retired in UI critique PR 2 once the e2e specs migrated).
      <button
        key={label}
        aria-label={label}
        aria-current={isActive ? "page" : undefined}
        onClick={() => onNavigate(page)}
        style={{
          flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", gap: 4, background: "none", border: "none",
          cursor: "pointer", fontFamily: "inherit", paddingTop: 6, paddingBottom: 6,
          color: isActive ? JT.text.primary : JT.text.tertiary,
        }}
      >
        <div style={{ width: 18, height: 3, borderRadius: 3, marginBottom: 2, background: isActive ? JT.brand.cta : "transparent" }} />
        {GLYPHS[label]}
        <div style={LABEL_STYLE}>{label}</div>
      </button>
    );
  };

  return (
    // position:relative + zIndex:20 give the bar its own stacking context above the
    // App.js scroll container (overflowY:auto + -webkit-overflow-scrolling:touch
    // promotes it to a composited layer on iOS). Without it, the composited scroll
    // layer paints over the raised IMPORT circle's protruding top half (marginTop:-14)
    // during scroll (#990). 20 > sticky header's 10, < BottomSheet modal's 200 — so
    // the bar stays above the scroll layer but modals still cover it.
    <nav aria-label="Primary" style={{
      height: 64,
      background: JT.bg.root,
      borderTop: `1px solid ${JT.border.default}`,
      display: "flex",
      alignItems: "stretch",
      position: "relative",
      zIndex: 20,
      paddingBottom: "max(12px, env(safe-area-inset-bottom))",
    }}>
      {navBtn(navSlots[0])}
      {navBtn(navSlots[1])}
      {/* Raised IMPORT — modal trigger, never active, label stays inactive-color */}
      <button
        key="IMPORT"
        aria-label="IMPORT"
        onClick={() => onImport()}
        style={{
          flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", gap: 4, background: "none", border: "none",
          cursor: "pointer", fontFamily: "inherit", padding: 0, color: JT.text.tertiary,
        }}
      >
        <div style={{
          width: 48, height: 48, borderRadius: "50%", marginTop: -14,
          background: "linear-gradient(135deg,#1d4ed8,#4f46e5)",
          boxShadow: JT.brand.ctaShadow,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: JT.text.primary,
        }}>
          {GLYPHS.IMPORT}
        </div>
        <div style={LABEL_STYLE}>IMPORT</div>
      </button>
      {navBtn(navSlots[2])}
      {navBtn(navSlots[3])}
    </nav>
  );
}
